
<template>
    <b-card no-body header-class="d-flex justify-content-between align-items-start" class="comment-card">
        <template v-slot:header>
            <h5>Comments ({{ commentsCounts }})</h5>

            <div class="d-flex justify-content-end align-items-center">
                <b-form-checkbox v-model="showRetractedComments" switch class="d-inline-block mr-2" v-if="!localOnly">
                    Show retracted comments
                </b-form-checkbox>
                <div class="mr-2" style="min-width: 200px;">
                    <b-input-group size="sm">
                        <b-form-input v-model="searchTerm" placeholder="Search comments..." @keydown.native="checkEnterSearch"></b-form-input>
                        <b-input-group-append>
                            <b-button class="search-button" v-on:click="searchComments"><i class="fas fa-search"></i></b-button>
                            <b-button class="search-button" v-on:click="clearSearch"><i class="fas fa-times"></i></b-button>
                        </b-input-group-append>
                    </b-input-group>
                </div>
                <div class="mr-2" style="min-width: 200px;" v-if="!localOnly && orderId!=null">
                    <select2 placeholder="All Worksheets"
                             disable-search
                             enable-ajax
                             allow-clear
                             key-property="workItemId"
                             text-property="displayName"
                             v-model="selectedWorkItemId"
                             css-class="form-control-sm"
                             dropdownCssClass="small"
                             v-on:search="getWorkItemOptions"
                             v-on:select="selectedWorkItemIdChanged"></select2>
                </div>
                <div class="mr-2" style="min-width: 200px;">
                    <select2 placeholder="All users"
                             enable-ajax
                             allow-clear
                             key-property="userAccountId"
                             text-property="displayName"
                             v-model="selectedUserAccountId"
                             css-class="form-control-sm"
                             dropdownCssClass="small"
                             v-on:search="findUserAccountOptions"
                             v-on:select="selectedUserAccountIdChanged"
                             v-if="!localOnly">
                    </select2>
                </div>
                <b-button size="sm" variant="outline-secondary" v-on:click="addComment" v-if="!disabled && canCreateComment" class="border-white text-white"><i class="fas fa-plus mr-1"></i>Add</b-button>
            </div>
        </template>

        <b-list-group flush v-bind:class="{ 'comment-list': !filteredCommentListEmpty, 'comment-list-empty': filteredCommentListEmpty }">

            <b-list-group-item v-if="commentListEmpty">
                The comments list is currently empty
            </b-list-group-item>
            <b-list-group-item v-if="!commentListEmpty && filteredCommentListEmpty">
                No comments to display
            </b-list-group-item>
            <template v-if="!filteredCommentListEmpty">

                <b-list-group-item class="d-flex justify-content-between align-items-start" v-for="comment in filteredCommentList" :key="comment.commentId">

                    <b-col cols="2" class="m-0 p-0 smaller">
                        <div v-if="!localOnly || hasCreatedDetails(comment)">by <strong>{{ comment.createdByUserDisplayName }}</strong> @ <strong>{{ formatDateTime(comment.createdDateTimeUTC, 'DD/MM/YYYY hh:mm a', true, false) }}</strong></div>
                        <b-badge variant="success" class="mr-1 p-1" v-if="(comment.commentType & ($enum.CommentType.Order | $enum.CommentType.WorkItem)) == ($enum.CommentType.Order | $enum.CommentType.WorkItem)">O + WS</b-badge>
                        <b-badge variant="primary" class="mr-1 p-1" v-if="(comment.commentType & $enum.CommentType.BillingInformation) == $enum.CommentType.BillingInformation">BI</b-badge>
                        <b-badge variant="warning" class="p-1" v-if="isOfOutsideOrigin(comment)">{{ renderOriginLabel(comment) }}</b-badge>
                    </b-col>

                    <b-col cols="8">
                        <div class="comment" v-bind:class="{ 'comment-active': !comment.disabledByUserDisplayName, 'comment-retracted': comment.disabledByUserDisplayName}">
                            <span v-html="comment.commentHtml"></span>
                        </div>
                    </b-col>

                    <b-col cols="2">
                        <div class="float-right">
                            <b-button-group v-if="canEditComment(comment) || canRetractComment(comment)">
                                <b-button variant="outline-secondary" title="Edit the comment" v-on:click="editComment(comment)" v-if="canEditComment(comment)" size="sm"><i class="fas fa-edit mr-1"></i>Edit</b-button>
                                <b-button variant="outline-danger" title="Retract the comment" v-on:click="retractComment(comment)" v-if="canRetractComment(comment)" size="sm"><i class="fas fa-times mr-1"></i>Retract</b-button>
                            </b-button-group>
                            <template v-if="comment.disabledDateTimeUTC != null">
                                <div class="smaller">{{ (comment.relatedCommentId != null) ? 'MODIFIED' : 'RETRACTED' }} by <strong>{{ comment.disabledByUserDisplayName }}</strong></div>
                                <div class="smaller font-weight-bold">{{ formatDateTime(comment.disabledDateTimeUTC, 'DD/MM/YYYY hh:mm a', true, false) }}</div>
                            </template>
                        </div>
                    </b-col>

                </b-list-group-item>

            </template>
        </b-list-group>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered :title="(currentComment.commentId == null) ? 'New Comment' : 'Edit Comment'"
                     v-model="modalDialogShown"
                     v-on:shown="onModalShown"
                     v-on:hidden="onModalHidden"
                     v-on:ok.prevent="passes(createOrUpdateComment)">

                <b-alert v-model="showErrors"
                         variant="danger"
                         dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>

                <b-form-row v-if="orderId != null || workItemId != null">
                    <div>
                        <b-form-group>
                            <b-form-checkbox v-model="currentComment.visibleInOrderAndWorksheet">
                                {{ getCheckBoxLabel() }}
                            </b-form-checkbox>
                            <b-form-checkbox v-model="currentComment.billingInformation" v-if="canCreateBillingInformationComment">
                                Billing Information
                            </b-form-checkbox>
                        </b-form-group>
                    </div>
                </b-form-row>
                <b-form-row>
                    <div id="defaultRTE" style="width: 100%;">
                        <ejs-richtexteditor ref="defaultRTE" :height="340" v-model="currentComment.commentHtml" :toolbarSettings="toolbarSettings">
                            {{ currentComment.commentHtml }}
                        </ejs-richtexteditor>
                    </div>
                </b-form-row>
            </b-modal>
        </validation-observer>
    </b-card>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { RichTextEditorPlugin, Toolbar, Link, Image, Count, HtmlEditor, QuickToolbar } from "@syncfusion/ej2-vue-richtexteditor";
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import Select2 from './Select2';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import UuidMixins from '../mixins/UuidMixins';

    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('select2', Select2);
    Vue.use(RichTextEditorPlugin);

    export default {
        name: 'Comments',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        model: {
            event: 'change',
            prop: 'comments'
        },
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            orderId: {
                type: String,
                required: false,
                default: null
            },
            workItemId: {
                type: String,
                required: false,
                default: null
            },
            quoteId: {
                type: String,
                required: false,
                default: null
            },
            comments: {
                type: Array,
                default: null
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canCreateBillingInformationComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
            disabled: {
                type: Boolean,
                default: false
            },
            defaultCommentType: {
                type: Number,
                required: true
            },
        },
        provide: {
            richtexteditor: [Toolbar, Link, Image, Count, HtmlEditor, QuickToolbar]
        },
        data: function () {
            return {
                showRetractedComments: true,
                commentList: this.comments,
                currentComment: { commentId: null, commentHtml: null, orderId: null, workItemId: null, quoteId: null, visibleInOrderAndWorksheet: false, billingInformation: false },
                currentErrors: [],
                modalDialogShown: false,
                selectedWorkItemId: null,
                selectedUserAccountId: null,
                searchTerm: null,

                isCommentOriginOutside: false,

                toolbarSettings: {
                    items: ['Bold', 'Italic', 'Underline', 'StrikeThrough',
                        'FontColor', '|', 'OrderedList', 'UnorderedList', '|',
                        'CreateLink', '|', 'ClearFormat', 'Undo', 'Redo', '|', 'FullScreen']
                }

            };
        },
        computed: {
            localOnly: function () {
                return this.isUuidNullOrEmpty(this.orderId) && this.isUuidNullOrEmpty(this.workItemId) && this.isUuidNullOrEmpty(this.quoteId);
            },

            showErrors: function () {
                return this.currentErrors.length > 0;
            },

            filteredCommentList: function () {

                if (this.commentList == null) return [];

                if (this.showRetractedComments) {
                    return this.commentList;
                } else {
                    var filteredComments = [];
                    for (var i = 0; i < this.commentList.length; i++) {
                        if (this.commentList[i].disabledDateTimeUTC == null) {
                            filteredComments.push(this.commentList[i]);
                        }
                    }
                    return filteredComments;
                }
            },

            commentListEmpty: function () {
                if (this.commentList == null) return true;
                return (this.commentList.length == 0);
            },

            filteredCommentListEmpty: function () {
                return (this.filteredCommentList.length == 0);
            },

            commentsCounts: function () {
                if (this.commentListEmpty) return '0';
                var filteredCount = this.filteredCommentList.length;
                var totalCount = this.commentList.length;
                if (filteredCount == totalCount) return totalCount;
                return filteredCount + '/' + (totalCount - filteredCount) + ' hidden';
            }
        },
        methods: {
            isOfOutsideOrigin: function (comment) {
                if (this.localOnly) return false;

                // we are on the order page
                if (!this.isUuidNullOrEmpty(this.orderId)) {
                    if (comment.orderId != null) return false;
                    else return true;
                }
                // we are on the worksheet page
                if (!this.isUuidNullOrEmpty(this.workItemId)) {

                    if (comment.workItemId != null) return false;
                    else return true;
                }
                // we are on the quote page
                if (!this.isUuidNullOrEmpty(this.quoteId)) {

                    if (comment.quoteId != null) return false;
                    else return true;
                }
            },
            hasCreatedDetails: function (comment) {
                return comment.createdByUserDisplayName && comment.createdDateTimeUTC;
            },
            renderOriginLabel: function (comment) {

                // we are on the order page
                if (!this.isUuidNullOrEmpty(this.orderId)) {
                    return comment.workItemDisplayName;
                }

                // we are on the worksheet page
                if (!this.isUuidNullOrEmpty(this.workItemId)) {
                    return comment.orderDisplayName;
                }

                // we are on the quote page
                if (!this.isUuidNullOrEmpty(this.quoteId)) {
                    return comment.quoteDisplayName;
                }
            },

            canEditComment: function (comment) {
                if (this.localOnly) {
                    if (comment.viewOnly) return false;
                    return true;
                }
                if (this.disabled) return false;
                if (comment.disabledDateTimeUTC != null) return false;
                if (this.canEditAndRetractAllComments) return true;
                return (comment.createdByUserAccountId == this.currentUserAccountId);
            },

            canRetractComment: function (comment) {
                if (this.localOnly) {
                    if (comment.viewOnly) return false;
                    return true;
                }

                if (this.disabled) return false;
                if (comment.disabledDateTimeUTC != null) return false;
                if (this.canEditAndRetractAllComments) return true;
                return (comment.createdByUserAccountId == this.currentUserAccountId);
            },
            onModalHidden() {
                this.$emit('modalOpen', false);
            },
            onModalShown() {
                this.$emit('modalOpen', true);
                this.$refs.defaultRTE.refreshUI();
                this.$refs.defaultRTE.focusIn();
            },
            selectedWorkItemIdChanged: function () {
                this.selectedUserAccountId = null;
                this.loadComments(true, this.searchTerm);
            },

            selectedUserAccountIdChanged: function () {
                this.selectedWorkItemId = null;
                this.loadComments(true, this.searchTerm);
            },

            reload: function (scrollToBottom = false) {
                var promises = [];
                promises.push(this.loadComments(scrollToBottom, this.searchTerm));
                return Promise.all(promises);
            },

            findUserAccountOptions: function (params, success, failure) {
                if (!this.isUuidNullOrEmpty(this.orderId) || !this.isUuidNullOrEmpty(this.workItemId) || !this.isUuidNullOrEmpty(this.quoteId)) {
                    var filter = {
                        searchTerm: ''
                    };
                    if (typeof (params.data.term) != "undefined")
                        filter.searchTerm = params.data.term;
                    var url = this.apiControllerUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'useraccountoptions/';
                    if (!this.isUuidNullOrEmpty(this.orderId)) url += 'byorderid/' + this.orderId;
                    else if (!this.isUuidNullOrEmpty(this.workItemId)) url += 'byworkitemid/' + this.workItemId;
                    else if (!this.isUuidNullOrEmpty(this.quoteId)) url += 'byquoteId/' + this.quoteId;
                    axios
                        .post(url, JSON.stringify(filter),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(response => {
                            var options = null;
                            if (response.data != null) {
                                options = response.data.map((obj) => {
                                    obj.id = obj.userAccountId;
                                    obj.text = obj.displayName;
                                    return obj;
                                });
                            }
                            success({ results: options });
                        })
                        .catch(function (error) {
                            failure(error);
                        });
                }
            },

            getWorkItemOptions: function (params, success, failure) {
                if (!this.isUuidNullOrEmpty(this.orderId)) {
                    this.currentErrors = [];
                    var url = this.apiControllerUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'workitemoptions/byorderid/' + this.orderId;

                    axios
                        .get(url)
                        .then(response => {
                            var options = null;
                            if (response.data != null) {
                                options = response.data.map((obj) => {
                                    obj.id = obj.workItemId;
                                    obj.text = obj.displayName;
                                    return obj;
                                });
                            }
                            success({ results: options });
                        })
                        .catch(function (error) {
                            failure(error);
                        });
                }
            },

            checkEnterSearch: function (event) {
                if (event.which === 13) {
                    this.searchComments();
                }
            },
            searchComments: function () {
                //if (this.searchTerm == null || this.searchTerm.trim() == "") return;
                this.loadComments(false, this.searchTerm);

            },
            clearSearch: function () {
                this.searchTerm = null;
                this.loadComments(false, this.searchTerm);
            },

            loadComments: function (scrollToBottom = false, searchTerm = null) {

                if (!this.isUuidNullOrEmpty(this.orderId) || !this.isUuidNullOrEmpty(this.workItemId) || !this.isUuidNullOrEmpty(this.quoteId)) {
                    this.currentErrors = [];
                    var url = this.apiControllerUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'list/';
                    if (!this.isUuidNullOrEmpty(this.orderId)) url += 'byorderid/' + this.orderId;
                    else if (!this.isUuidNullOrEmpty(this.workItemId)) url += 'byworkItemId/' + this.workItemId;
                    else if (!this.isUuidNullOrEmpty(this.quoteId)) url += 'byquoteId/' + this.quoteId;
                    if (this.selectedWorkItemId) url += '/filterbyworkitemid/' + this.selectedWorkItemId
                    else if (this.selectedUserAccountId) url += '/filterbyuseraccountid/' + this.selectedUserAccountId
                    if (searchTerm != null) {
                        var search = encodeURIComponent(searchTerm);
                        url += "/" + search;
                    }
                    console.log(url);
                    return axios
                        .get(url)
                        .then(response => {
                            this.commentList = response.data;
                            if (scrollToBottom) {
                                Vue.nextTick(() => {
                                    this.scrollToBottomOfComments();
                                });
                            }
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length != 0) {
                                this.showErrorsToast(this.currentErrors);
                            }
                        });
                }
                return Promise.resolve();

            },

            scrollToBottomOfComments: function () {
                var commentsList = document.getElementsByClassName("comment-list");
                if (!commentsList || (commentsList.length == 0)) return;
                commentsList = commentsList[0];
                commentsList.scrollTop = commentsList.scrollHeight;
            },

            addComment: function () {
                this.currentComment = { commentId: null, commentHtml: null, visibleInOrderAndWorksheet: false, billingInformation: false };
                this.currentErrors = [];
                this.modalDialogShown = true;
            },

            editComment: function (comment) {
                this.currentComment = {
                    commentId: comment.commentId,
                    commentHtml: comment.commentHtml,
                    visibleInOrderAndWorksheet: (comment.commentType & (this.$enum.CommentType.Order | this.$enum.CommentType.WorkItem)) == (this.$enum.CommentType.Order | this.$enum.CommentType.WorkItem),
                    billingInformation: (comment.commentType & this.$enum.CommentType.BillingInformation) == this.$enum.CommentType.BillingInformation
                };
                this.currentErrors = [];
                this.modalDialogShown = true;
            },

            retractComment: function (comment) {
                if (!this.localOnly) {
                    this.currentErrors = [];
                    axios
                        .delete(this.apiControllerUrl + '/' + comment.commentId)
                        .then(() => {
                            this.loadComments(false, this.searchTerm);
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length != 0) {
                                this.showErrorsToast(this.currentErrors);
                            }
                        });
                } else {
                    if (this.commentList == null) this.commentList = [];
                    var index = this.commentList.findIndex(c => c.commentId == comment.commentId);
                    if (index >= 0) {
                        this.commentList.splice(index, 1);
                        this.$emit('change', this.commentList);
                    }
                }
            },
            createOrUpdateComment: function () {
                var data = {
                    commentId: this.currentComment.commentId,
                    orderId: this.isUuidNullOrEmpty(this.orderId) ? null : this.orderId,
                    workItemId: this.isUuidNullOrEmpty(this.workItemId) ? null : this.workItemId,
                    quoteId: this.isUuidNullOrEmpty(this.quoteId) ? null : this.quoteId,
                    commentHtml: this.currentComment.commentHtml
                }
                if (this.currentComment.visibleInOrderAndWorksheet) data.commentType = this.$enum.CommentType.Order | this.$enum.CommentType.WorkItem;
                else if (!this.isUuidNullOrEmpty(this.orderId)) data.commentType = this.$enum.CommentType.Order;
                else if (!this.isUuidNullOrEmpty(this.workItemId)) data.commentType = this.$enum.CommentType.WorkItem;
                else if (!this.isUuidNullOrEmpty(this.quoteId)) data.commentType = this.$enum.CommentType.Quote;
                else data.commentType = this.defaultCommentType;
                if (this.currentComment.billingInformation) data.commentType |= this.$enum.CommentType.BillingInformation;
                if (!this.localOnly) {
                    var myUrl = this.apiControllerUrl + '/create';
                    if (this.currentComment.commentId != null) myUrl = this.apiControllerUrl + '/update';

                    this.currentErrors = [];
                    axios
                        .request(
                            {
                                method: (this.currentComment.commentId == null) ? 'post' : 'put',
                                url: myUrl,
                                data: JSON.stringify(data),
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(() => {
                            this.reload(true);
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length == 0) {
                                this.modalDialogShown = false;
                            }
                        })
                } else {
                    if (this.commentList == null) this.commentList = [];
                    if (!data.commentId) { //this is a new comment
                        data.commentId = this.newUuid(); //we generate a UUID so that we can detect an edit or retract operation later
                        this.commentList.push({
                            ...data
                        });
                    } else {
                        var comment = this.commentList.find(c => c.commentId == data.commentId);
                        if (comment) {
                            comment.commentHtml = data.commentHtml;
                            comment.commentType = data.commentType;
                        }
                    }
                    this.$emit('change', this.commentList);
                    this.modalDialogShown = false;
                }
            },

            getCheckBoxLabel: function () {
                if (this.orderId != null) {
                    return ('Visible in Worksheets');
                } else if (this.workItemId != null) {
                    return ('Visible in Order');
                } else {
                    return ('Invalid configuration');
                }
            }
        },

        mounted: function () {
            this.reload(true);
        }
    }
</script>

<style scoped>

    .comment-card {
        background-color: #00a8eb !important;
    }

        .comment-card .card-header {
            color: white;
        }

    .comment-list {
        overflow-y: auto;
        resize: vertical;
        background: #fff;
        min-height: 200px;
        height: 350px;
    }

    .comment-list-empty {
        height: auto !important;
    }

    .comment {
        border-radius: 5px;
        padding: 0px 5px 0px 5px;
    }

    .comment-active {
        background-color: white;
    }

    .comment-retracted {
        text-decoration: line-through;
        background-color: #eee;
    }

    .smaller {
        font-size: 12px;
        color: #000;
    }

    .search-button, .search-button:focus {
        color: #ced4da;
        background-color: #fff;
        border: 1px solid #ced4da;
        border-left: none;
        border-right: none;
        box-shadow: none !important;
    }

        .search-button:hover, .search-button:active {
            color: #fff;
            background-color: #ced4da;
            border: 1px solid #ced4da;
            border-left: none;
            border-right: none;
            box-shadow: none !important;
        }

        .search-button:active {
            background-color: #b9c1c9 !important;
            border-color: #b9c1c9 !important;
        }
</style>
